import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarPlus } from "lucide-react";
import { api, errMsg, useApi, useSeo, eur, fileUrl, fmtLocal, uid, BASE } from "../lib/api";
import { useCart } from "../lib/store";
import { ArtCard, EventCard, FindCard, Loading, Page, PageTitle, Empty, MapButton, WhatsAppButton, Prose, ArtImage } from "../components/site/Bits";
import { InquiryDialog, InquiryForm } from "../components/site/Inquiry";

import { useSpamProtection } from "../components/site/SpamProtection";

export function Finds() {
  const [cat, setCat] = useState("");
  const { data, loading } = useApi(`/public/finds?category=${encodeURIComponent(cat)}`);
  useSeo("Fundstücke", "Restaurierte Einzelstücke von George.");
  return (
    <Page>
      <PageTitle eyebrow="Georges Fundstücke" title="Fundstücke">Restaurierte Möbel und Objekte – jedes ein Einzelstück mit ehrlicher Zustandsbeschreibung.</PageTitle>
      <div className="flex flex-wrap gap-2 mb-10" role="group" aria-label="Kategorie">
        {["", ...(data?.categories || [])].map((c) => <button key={c || "all"} onClick={() => setCat(c)} aria-pressed={cat === c} className={`h-10 px-4 border font-semibold text-sm ${cat === c ? "bg-[#121212] text-white" : "border-[#E6DFD5] bg-white"}`} data-testid={`find-cat-${c || "all"}`}>{c || "Alle"}</button>)}
      </div>
      {loading ? <Loading /> : !data?.items.length ? <Empty>Keine Fundstücke in dieser Kategorie.</Empty> : <div className="grid gap-8 grid-cols-2 lg:grid-cols-4">{data.items.map((f) => <FindCard key={f.id} f={f} />)}</div>}
    </Page>
  );
}

export function FindDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { add } = useCart();
  const { data: f, loading, error } = useApi(`/public/finds/${slug}`);
  const [open, setOpen] = useState(false);
  const [img, setImg] = useState(0);
  useEffect(() => {
    if (f?.redirect) nav(`/fundstuecke/${f.redirect}`, { replace: true });
  }, [f, nav]);
  useSeo(f?.title, f?.description?.slice(0, 150));
  if (loading || f?.redirect) return <Loading />;
  if (error) return <Page><Empty>Fundstück nicht gefunden.</Empty></Page>;
  const avail = (f.stock_available ?? 0) > 0 && !f.sold;
  return (
    <Page>
      <div className="grid lg:grid-cols-2 gap-12">
        <div><ArtImage image={f.images?.[img]} ratio="aspect-square" eager testid="find-main-image" />
          <div className="flex gap-2 mt-3">{(f.images || []).map((m, i) => <button key={m.id} onClick={() => setImg(i)} className="passe h-16 w-16 relative" aria-label={`Bild ${i + 1}`}><img src={fileUrl(m.thumb)} alt="" className="absolute inset-0 w-full h-full object-contain" /></button>)}</div></div>
        <div className="space-y-5">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0047FF]">{f.category} · {f.unique ? "Einzelstück" : `Bestand ${f.stock_available}`}</p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight" data-testid="find-title">{f.title}</h1>
          <p className="text-2xl font-bold" data-testid="find-price">{!avail ? "Verkauft" : f.price ? eur(f.price) : "Preis auf Anfrage"}</p>
          <Prose text={f.description} />
          <dl className="grid grid-cols-2 gap-2 text-sm">
            {(f.width || f.height) && <><dt className="text-[#6E6B65]">Maße</dt><dd>{f.width} × {f.height}{f.depth ? ` × ${f.depth}` : ""} {f.unit || "cm"}</dd></>}
            {f.material && <><dt className="text-[#6E6B65]">Material</dt><dd>{f.material}</dd></>}
            {f.condition && <><dt className="text-[#6E6B65]">Zustand</dt><dd>{f.condition}</dd></>}
            {f.restoration && <><dt className="text-[#6E6B65]">Restaurierung</dt><dd>{f.restoration}</dd></>}
            <dt className="text-[#6E6B65]">Lieferung</dt><dd>{[f.shipping && "Versand", f.pickup && "Abholung"].filter(Boolean).join(" oder ") || "nach Absprache"}</dd>
            <dt className="text-[#6E6B65]">Anbieter</dt><dd>{f.provider_name || "–"}</dd>
          </dl>
          {f.defects && <p className="bg-[#FFEAEA] border-l-4 border-[#FF5A5F] p-3 text-sm" data-testid="find-defects"><strong>Bekannte Mängel:</strong> {f.defects}</p>}
          {avail && (
            <div className="flex flex-wrap gap-3">
              {f.buyable && <button onClick={() => { add({ kind: "find", find_id: f.id, title: f.title, label: "Fundstück", image: f.images?.[0]?.thumb }); toast.success("In den Warenkorb gelegt"); }} className="btn-coral h-12 px-5 font-semibold" data-testid="buy-find">In den Warenkorb</button>}
              <button onClick={() => setOpen(true)} className="btn-ink h-12 px-5 font-semibold" data-testid="inquire-find">Artikel anfragen</button>
              <WhatsAppButton area="finds" text={`Hallo George! Interesse an „${f.title}“: ${window.location.origin}/fundstuecke/${f.slug}`} testid="find-whatsapp" />
            </div>
          )}
        </div>
      </div>
      <InquiryDialog open={open} onOpenChange={setOpen} title="Fundstück anfragen" type="find" object={{ type: "find", id: f.id, title: f.title }} />
    </Page>
  );
}

const KIND = { wall_concept: "Wandkonzept", compilation: "Zusammenstellung", onsite: "Umsetzung vor Ort" };

export function Interior() {
  const { data, loading } = useApi("/public/interior");
  const [sp] = useSearchParams();
  const workSlug = sp.get("werk");
  const { data: work, loading: workLoading, error: workError } = useApi(workSlug ? `/public/artworks/${encodeURIComponent(workSlug)}` : null);
  const { data: finds } = useApi("/public/finds");
  const selectedWork = work && !work.redirect ? work : null;
  useSeo("Vintage-Einrichtung mit George", "Dein Kunstwerk als Ausgangspunkt: George gestaltet mit dir Wand und Raum mit Vintage-Möbeln und Fundstücken.");
  if (loading) return <Loading />;
  return (
    <Page>
      <section className="bg-[#F4D9D9] border-2 border-[#121212] p-6 sm:p-12 mb-12">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold">Vintage-Einrichtung mit George</p>
        <h1 className="text-4xl sm:text-6xl font-black mt-4 max-w-3xl">Dein Bild. Dein Raum. Dein Stil.</h1>
        <p className="mt-6 text-lg max-w-2xl">Ein Kunstwerk kann der Anfang sein. George gestaltet mit dir den Bereich rundherum: mit charaktervollen Vintage-Möbeln, restaurierten Fundstücken und einer Zusammenstellung, die zu dir passt.</p>
        <a href="#einrichtungsanfrage" className="btn-ink inline-flex items-center min-h-12 px-6 py-3 font-semibold mt-6">Raumfoto schicken & Angebot anfragen</a>
      </section>
      <section className="mb-14" aria-labelledby="interior-process">
        <h2 id="interior-process" className="text-3xl font-black mb-6">So entsteht dein Raum</h2>
        <ol className="grid md:grid-cols-3 gap-6">
          {[["01", "Zeig uns deinen Raum", "Schick Fotos, deinen Ort und deine Wünsche. Ein ausgewähltes Kunstwerk nehmen wir direkt in die Anfrage mit."],
            ["02", "Gemeinsam abstimmen", "George bespricht mit dir die Möglichkeiten. Du bekommst ein Angebot mit vereinbartem Umfang und Kosten."],
            ["03", "Passend umsetzen", "Nach deiner Beauftragung geht es an die vereinbarte Gestaltung. Möbel, Anfahrt und Montage werden ausdrücklich abgestimmt."]].map(([n, title, text]) => <li key={n} className="border-t-2 border-[#121212] pt-4"><p className="text-[#0047FF] font-semibold">{n}</p><h3 className="text-xl font-black mt-2">{title}</h3><p className="mt-2 text-sm leading-relaxed">{text}</p></li>)}
        </ol>
      </section>
      {data.page && <div className="max-w-3xl mb-14"><Prose text={data.page.body} /></div>}
      <div className="grid gap-6 md:grid-cols-3 mb-20">{data.services.map((s, i) => (
        <article key={s.id} className={`p-6 border-2 ${i === 1 ? "bg-[#FFC72C] border-[#FFC72C]" : "bg-white border-[#121212]"}`} data-testid={`service-${s.slug}`}>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold">{KIND[s.kind] || s.kind}</p>
          <h2 className="text-2xl font-black mt-2">{s.title}</h2>
          <p className="mt-3 text-sm">{s.description}</p>
          <ul className="mt-4 text-sm space-y-1">
            {s.includes && <li><strong>Leistung:</strong> {s.includes}</li>}
            {s.revision_rounds && <li><strong>Korrekturrunden:</strong> {s.revision_rounds}</li>}
            {s.travel_note && <li><strong>Anfahrt:</strong> {s.travel_note}</li>}
            {s.material_note && <li><strong>Material:</strong> {s.material_note}</li>}
            {s.item_cost_note && <li><strong>Artikelkosten:</strong> {s.item_cost_note}</li>}
          </ul>
          <p className="mt-4 font-bold" data-testid={`service-price-${s.slug}`}>{s.price_agreed && s.fixed_price ? `Fixpreis ${eur(s.fixed_price)}` : "Angebot auf Anfrage"}</p>
        </article>))}</div>
      {(finds?.items || []).length > 0 && <section className="mb-16"><h2 className="text-3xl font-black">Fundstücke mit Charakter</h2><p className="mt-3 mb-6">Einzeln entdecken oder als Ausgangspunkt für deine Einrichtung. Die konkrete Auswahl stimmen wir mit dir ab.</p><div className="grid grid-cols-2 lg:grid-cols-4 gap-6">{finds.items.slice(0, 4).map((f) => <FindCard key={f.id} f={f} />)}</div><Link to="/fundstuecke" className="inline-block underline font-semibold mt-5">Alle Fundstücke ansehen</Link></section>}
      <div id="einrichtungsanfrage" className="scroll-mt-8 grid lg:grid-cols-[1fr_1.4fr] gap-10 bg-[#F4EFEA] p-6 sm:p-10">
        <div><h2 className="text-3xl font-black">Angebot anfragen</h2><p className="mt-3 text-sm">Schick uns Raumfotos, Budget und Zeitraum. Fotos bleiben intern und werden nicht veröffentlicht.</p>
          <div className="mt-4"><WhatsAppButton area="interior" text="Hallo George! Ich interessiere mich für eine Einrichtungsberatung." testid="interior-whatsapp" /></div></div>
        {workSlug && workLoading ? <Loading /> : <div>
          {workSlug && (workError || !selectedWork) && <p className="mb-4 text-sm" role="status">Das ausgewählte Werk ist derzeit nicht verfügbar. Du kannst trotzdem eine allgemeine Einrichtungsanfrage senden.</p>}
          {selectedWork && <Link to={`/kunst/${selectedWork.slug}`} className="block underline font-semibold mb-4">Dein ausgewähltes Werk: {selectedWork.title}</Link>}
          <InquiryForm key={selectedWork?.id || "interior"} type="interior" object={selectedWork ? { type: "artwork", id: selectedWork.id, title: selectedWork.title } : { type: "interior", title: "Einrichtung mit George" }} defaultMessage={selectedWork ? `Ich interessiere mich für eine Einrichtung rund um „${selectedWork.title}“.` : ""} />
        </div>}
      </div>
    </Page>
  );
}

export function Events() {
  const { data, loading } = useApi("/public/events");
  useSeo("Vernissagen", "Kommende und vergangene Vernissagen und Veranstaltungen.");
  if (loading) return <Loading />;
  return (
    <Page>
      <PageTitle eyebrow="Vernissagen & Veranstaltungen" title="Termine">Alle Zeiten in Wiener Zeit (Europe/Vienna).</PageTitle>
      {!data.upcoming.length ? <Empty>Derzeit keine kommenden Termine.</Empty> : <div className="grid md:grid-cols-2 gap-4" data-testid="events-upcoming">{data.upcoming.map((e) => <EventCard key={e.id} e={e} />)}</div>}
      {data.past.length > 0 && <section className="mt-16" data-testid="events-past"><h2 className="text-xl font-black text-[#6E6B65] mb-6">Archiv</h2><div className="grid md:grid-cols-2 gap-4 opacity-75">{data.past.map((e) => <EventCard key={e.id} e={e} />)}</div></section>}
    </Page>
  );
}

function Registration({ e, onDone }) {
  const spam = useSpamProtection();
  const token = useRef(uid());
  const [f, setF] = useState({ name: "", email: "", persons: 1 });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const submit = async (ev) => {
    ev.preventDefault();
    if (busy || !spam.ready) return;
    setBusy(true);
    try {
      const r = await api.post(`/public/events/${e.slug}/register`, { ...f, persons: Number(f.persons), turnstile_token: spam.token, client_token: token.current });
      setRes(r.data);
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      spam.reset();
      setBusy(false);
    }
  };
  if (res) return <p className="bg-[#EBF0FF] p-4 text-sm" data-testid="registration-success">Angemeldet! {res.registration.mail_status === "sent" ? "Bestätigung per E-Mail ist unterwegs." : "Hinweis: E-Mail-Bestätigung derzeit nicht eingerichtet – deine Anmeldung ist gespeichert."}</p>;
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-[1fr_1fr_90px_auto] gap-3 items-end" data-testid="registration-form">
      <label className="text-sm font-semibold">Name<input required minLength={2} value={f.name} onChange={(x) => setF({ ...f, name: x.target.value })} className="h-11 w-full border border-[#E6DFD5] px-3 font-normal" data-testid="reg-name" /></label>
      <label className="text-sm font-semibold">E-Mail<input required type="email" value={f.email} onChange={(x) => setF({ ...f, email: x.target.value })} className="h-11 w-full border border-[#E6DFD5] px-3 font-normal" data-testid="reg-email" /></label>
      <label className="text-sm font-semibold">Personen<input type="number" min={1} max={10} value={f.persons} onChange={(x) => setF({ ...f, persons: x.target.value })} className="h-11 w-full border border-[#E6DFD5] px-3 font-normal" data-testid="reg-persons" /></label>
      <div className="sm:col-span-full">{spam.widget}</div>
      <button disabled={busy || !spam.ready} className="btn-ink h-11 px-5 font-semibold" data-testid="reg-submit">{busy ? "…" : "Anmelden"}</button>
    </form>
  );
}

export function EventDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { data: e, loading, error, reload } = useApi(`/public/events/${slug}`);
  useEffect(() => {
    if (e?.redirect) nav(`/vernissagen/${e.redirect}`, { replace: true });
  }, [e, nav]);
  useSeo(e?.title, e?.description?.slice(0, 150), e?.title ? { "@context": "https://schema.org", "@type": "Event", name: e.title, startDate: e.start, endDate: e.end || undefined,
    eventStatus: e.state === "cancelled" ? "https://schema.org/EventCancelled" : "https://schema.org/EventScheduled", location: { "@type": "Place", name: e.venue?.name || e.location_text, address: e.address || e.venue?.address } } : null);
  if (loading || e?.redirect) return <Loading />;
  if (error) return <Page><Empty>Veranstaltung nicht gefunden.</Empty></Page>;
  const cancelled = e.state === "cancelled";
  return (
    <Page>
      {cancelled && <p className="bg-[#FF5A5F] text-[#121212] font-bold px-4 py-3 mb-8 text-lg" data-testid="event-cancelled">Diese Veranstaltung wurde abgesagt.</p>}
      {e.state === "past" && <p className="bg-[#F4EFEA] px-4 py-3 mb-8 font-semibold" data-testid="event-past">Diese Veranstaltung hat bereits stattgefunden.</p>}
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-12">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0047FF] mb-3">Vernissage</p>
          <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight ${cancelled ? "line-through decoration-[#FF5A5F]" : ""}`} data-testid="event-title">{e.title}</h1>
          <p className="mt-5 text-lg font-semibold" data-testid="event-time">{fmtLocal(e.start)}{e.end ? ` – ${e.end.slice(0, 10) === e.start.slice(0, 10) ? e.end.slice(11, 16) + " Uhr" : fmtLocal(e.end)}` : ""} <span className="text-sm font-normal text-[#6E6B65]">(Europe/Vienna)</span></p>
          <p className="mt-1">{e.venue ? <Link to={`/lokale/${e.venue.slug}`} className="underline">{e.venue.name}</Link> : e.location_text} · {e.address || [e.venue?.address, e.venue?.city].filter(Boolean).join(", ")}</p>
          <div className="mt-5 flex flex-wrap gap-4 items-center">
            <a href={`${BASE}/api/public/events/${e.slug}/ics`} className="inline-flex items-center gap-2 h-12 px-5 border-2 border-[#121212] font-semibold hover:bg-[#121212] hover:text-white transition-colors" data-testid="event-ics"><CalendarPlus size={18} /> In Kalender (.ics)</a>
            <MapButton venue={e.venue || { name: e.location_text, address: e.address }} />
          </div>
          <div className="mt-8"><Prose text={e.description} /></div>
          {!cancelled && e.state !== "past" && (
            <div className="mt-10 border-t border-[#E6DFD5] pt-8">
              {e.registration_enabled ? (<><h2 className="text-2xl font-black mb-2">Anmeldung</h2>
                <p className="text-sm mb-4" data-testid="event-spots">{e.spots_left != null ? `Noch ${e.spots_left} Plätze frei.` : "Anmeldung erbeten."}</p>
                {e.spots_left === 0 ? <p className="font-semibold">Ausgebucht.</p> : <Registration e={e} onDone={reload} />}</>)
                : <p className="font-semibold" data-testid="event-free">Freier Eintritt – keine Anmeldung nötig.</p>}
            </div>
          )}
        </div>
        <div>{e.image && <ArtImage image={e.image} />}
          {e.artists.length > 0 && <div className="mt-6"><p className="text-sm font-semibold mb-2">Beteiligte Künstler</p>{e.artists.map((a) => <Link key={a.id} to={`/kuenstler/${a.slug}`} className="block link-u text-lg font-bold">{a.name}</Link>)}</div>}</div>
      </div>
      {e.works.length > 0 && <section className="mt-20"><h2 className="text-2xl font-black mb-8">Gezeigte Werke</h2><div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">{e.works.map((w) => <ArtCard key={w.id} a={w} />)}</div></section>}
    </Page>
  );
}

